// AuthContext — глобальный auth state для React-приложения «Упряжка».
// Управляет currentUser + login/logout/refresh + bootstrap через /api/auth/me.

import { createContext, useCallback, useContext, useEffect, useReducer, type ReactNode } from 'react';
import { jwtStorage } from './jwtStorage';
import { authApi, type CurrentUserView, type LoginRequest } from '@/api/auth';
import { setOnUnauthorized } from '@/api/client';

/** Статус аутентификации. */
export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

/** Состояние AuthContext. */
export interface AuthState {
  status: AuthStatus;
  currentUser: CurrentUserView | null;
  error: string | null;
}

/** Действия для useReducer. */
type AuthAction =
  | { type: 'BOOTSTRAP_START' }
  | { type: 'BOOTSTRAP_SUCCESS'; user: CurrentUserView }
  | { type: 'BOOTSTRAP_FAILURE' }
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; user: CurrentUserView }
  | { type: 'LOGIN_FAILURE'; error: string }
  | { type: 'LOGOUT' }
  | { type: 'USER_UPDATED'; user: CurrentUserView }
  | { type: 'CLEAR' };

const initialState: AuthState = {
  status: 'initializing',
  currentUser: null,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'BOOTSTRAP_START':
    case 'LOGIN_START':
      return { ...state, status: state.status === 'initializing' ? 'initializing' : state.status, error: null };
    case 'BOOTSTRAP_SUCCESS':
    case 'LOGIN_SUCCESS':
      return { status: 'authenticated', currentUser: action.user, error: null };
    case 'BOOTSTRAP_FAILURE':
      return { status: 'unauthenticated', currentUser: null, error: null };
    case 'LOGIN_FAILURE':
      return { ...state, error: action.error };
    case 'LOGOUT':
    case 'CLEAR':
      return { status: 'unauthenticated', currentUser: null, error: null };
    // R39b / U-10: self-service обновление профиля — обновляем currentUser без перелогина.
    case 'USER_UPDATED':
      return state.status === 'authenticated' ? { ...state, currentUser: action.user } : state;
    default:
      return state;
  }
}

/** Значение AuthContext. */
export interface AuthContextValue extends AuthState {
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  /** Обновить currentUser после обновления профиля (R39b / U-10). */
  updateCurrentUser: (user: CurrentUserView) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Provider AuthContext. */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Bootstrap: при mount, если токены есть → /api/auth/me, иначе → unauthenticated.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap(): Promise<void> {
      if (!jwtStorage.hasTokens()) {
        dispatch({ type: 'BOOTSTRAP_FAILURE' });
        return;
      }
      dispatch({ type: 'BOOTSTRAP_START' });
      try {
        const user = await authApi.me();
        if (!cancelled) dispatch({ type: 'BOOTSTRAP_SUCCESS', user });
      } catch {
        if (!cancelled) dispatch({ type: 'BOOTSTRAP_FAILURE' });
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  // При 401 + refresh failure → clearTokens + CLEAR.
  useEffect(() => {
    setOnUnauthorized(() => {
      jwtStorage.clearTokens();
      dispatch({ type: 'CLEAR' });
    });
  }, []);

  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const tokens = await authApi.login(credentials);
      jwtStorage.saveTokens(tokens.accessToken, tokens.refreshToken);
      const user = await authApi.me();
      dispatch({ type: 'LOGIN_SUCCESS', user });
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'LOGIN_FAILURE', error: message });
      return false;
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      if (jwtStorage.hasTokens()) {
        await authApi.logout();
      }
    } catch {
      // Игнорируем ошибки logout — клиент всё равно очищает локальное состояние.
    }
    jwtStorage.clearTokens();
    dispatch({ type: 'LOGOUT' });
  }, []);

  // R39b / U-10: после PATCH /api/users/me — обновляем currentUser без перелогина.
  const updateCurrentUser = useCallback((user: CurrentUserView): void => {
    dispatch({ type: 'USER_UPDATED', user });
  }, []);

  const value: AuthContextValue = { ...state, login, logout, updateCurrentUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Хук для доступа к AuthContext. */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth должен вызываться внутри AuthProvider');
  }
  return ctx;
}
